/**
 * BrandLogo — single source of truth for the RV Match A.I. wordmark.
 *
 * Style mirrors the product-box visual identity: a cyan→sky→indigo
 * gradient "RV" mark + "RV Match A.I." typography with a subtle gradient
 * accent on "A.I." Everything is pure SVG/CSS so it scales cleanly,
 * stays crisp at any DPI, and never depends on a hosted asset.
 *
 * Sizes:
 *   sm  → mark only or compact wordmark (32px mark)
 *   md  → default header use (36px mark) — public nav, dealer sidebar
 *   lg  → large hero / standalone uses (48px mark)
 *
 * Variants:
 *   wordmark  → mark + full text
 *   mark      → just the gradient "RV" tile (great for tight icons / favicons)
 *   stacked   → mark on top, wordmark below (sign-in cards / ebook covers)
 *
 * Theme:
 *   dark  → designed for cinematic dark backgrounds (default)
 *   light → flips wordmark color so it reads on white surfaces
 */

import React from 'react';

const SIZE_MAP = {
  sm: { mark: 'w-8 h-8 text-xs', text: 'text-sm', gap: 'gap-2' },
  md: { mark: 'w-9 h-9 text-sm', text: 'text-base sm:text-lg', gap: 'gap-2' },
  lg: { mark: 'w-12 h-12 text-base', text: 'text-xl sm:text-2xl', gap: 'gap-3' },
};

const Mark = ({ size = 'md', className = '' }) => {
  const s = SIZE_MAP[size] || SIZE_MAP.md;
  return (
    <span
      className={`inline-flex items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 via-sky-400 to-indigo-400 shadow-[0_0_22px_-4px_rgba(56,189,248,0.65)] text-[#05070d] font-black tracking-tight ${s.mark} ${className}`}
      aria-hidden="true"
    >
      RV
    </span>
  );
};

const Wordmark = ({ size = 'md', theme = 'dark' }) => {
  const s = SIZE_MAP[size] || SIZE_MAP.md;
  const baseColor = theme === 'light' ? 'text-slate-900' : 'text-white';
  return (
    <span className={`font-extrabold tracking-tight whitespace-nowrap ${baseColor} ${s.text}`}>
      RV Match{' '}
      <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-300 to-indigo-300">
        A.I.
      </span>
    </span>
  );
};

const BrandLogo = ({ size = 'md', variant = 'wordmark', theme = 'dark', className = '' }) => {
  const s = SIZE_MAP[size] || SIZE_MAP.md;

  if (variant === 'mark') {
    return <Mark size={size} className={className} />;
  }

  if (variant === 'stacked') {
    return (
      <div className={`inline-flex flex-col items-center gap-2 ${className}`} data-testid="brand-logo-stacked">
        <Mark size={size === 'sm' ? 'md' : 'lg'} />
        <Wordmark size={size} theme={theme} />
      </div>
    );
  }

  // Default: horizontal wordmark
  return (
    <span className={`inline-flex items-center ${s.gap} ${className}`} data-testid="brand-logo">
      <Mark size={size} />
      <Wordmark size={size} theme={theme} />
    </span>
  );
};

export default BrandLogo;
export { Mark as BrandMark, Wordmark as BrandWordmark };
